import React from "react";
import PropTypes from "prop-types";
import { NoPending, NoPendingFiltered } from "components/documents/documents-grid/no-documents";
import { categoryType, currencyType, dataType, documentPropType, vendorType } from "custom-prop-types";
import grid from "components/grid.scss";
import FaFilter from "react-icons/fa/filter";
import styles from "components/upload-documents/pending-documents.scss";
import { pluralize } from "utils/string-helpers";
import { AwaitingProcessing } from "components/upload-documents/awaiting-processing";
import { ProcessingFields } from "components/upload-documents/processing-fields";
import { ProcessingViewer } from "components/upload-documents/processing-viewer";
import { CancelProcessingModal } from "components/upload-documents/cancel-processing-modal";
import { TrashDocumentModal } from "components/documents/modals/trash-document-modal";
import { ProcessingToggle } from "components/upload-documents/processing-toggle";
import { UploadInlineTabs } from "components/upload-documents/upload-inline-tabs";
import { DigitalUploadFilters } from "components/upload-documents/digital-upload-filters";
import { Drawer } from "components/common/drawer";

export const PendingDocuments = (props) => {
  if (props.pendingDocuments.length === 0 && props.sourceFilter === null && props.statusFilter === null) {
    return (
      <div className={ styles["no-pending-container"] }>
        <UploadInlineTabs accountSlug={ props.accountSlug } currentPage="awaiting-digital" />
        <NoPending accountSlug={ props.accountSlug } />
      </div>
    );
  }
  return (
    <div>
      <Drawer
        isDrawerMode={ props.isFiltersMode }
        onEndDrawerMode={ props.onEndFiltersMode }
      >
        <DigitalUploadFilters { ...props } />
      </Drawer>
      <UploadInlineTabs accountSlug={ props.accountSlug } currentPage="awaiting-digital" />
      <div className={ styles["title-area"] }>
        <h2 className={ styles["processing-title"] }>
          {`${pluralize(props.pendingDocuments.length, "documents", "document")} awaiting processing`}
        </h2>
        <div className={ styles["processing-toggle"] }>
          <button className={ styles.filters } onClick={ props.onStartFiltersMode }>
            <FaFilter className={ styles.icon } size={ 15 } />
            Filters
          </button>
          <ProcessingToggle
            isBeingCancelled={ props.isBeingCancelled }
            isCancelProcessingLoading={ props.isCancelProcessingLoading }
            onCancelProcessingAllDocuments={ props.onCancelProcessingAllDocuments }
            onProcessAllDocuments={ props.onProcessAllDocuments }
            pendingDocuments={ props.pendingDocuments }
          />
        </div>
      </div>
      <div className={ `${grid.row} ${styles["pending-documents-container"]}` }>
        <PendingDocumentsProcessingArea { ...props } />
      </div>
    </div>
  );
};

PendingDocuments.defaultProps = {
  focusedDocument: null,
  pendingDocuments: [],
  sourceFilter: null,
  statusFilter: null
};

PendingDocuments.propTypes = {
  accountSlug: PropTypes.string.isRequired,
  isBeingCancelled: PropTypes.bool.isRequired,
  isCancelProcessingLoading: PropTypes.bool.isRequired,
  isFiltersMode: PropTypes.bool.isRequired,
  onCancelProcessingAllDocuments: PropTypes.func.isRequired,
  onEndFiltersMode: PropTypes.func.isRequired,
  onProcessAllDocuments: PropTypes.func.isRequired,
  onStartFiltersMode: PropTypes.func.isRequired,
  pendingDocuments: PropTypes.arrayOf(documentPropType),
  sourceFilter: dataType,
  statusFilter: dataType
};

const PendingDocumentsProcessingArea = (props) => {
  if (props.pendingDocuments.length === 0) {
    return (
      <div className={ `${styles["no-pending-container"]} ${styles["no-filtered"]}` }>
        <NoPendingFiltered />
      </div>
    );
  }
  return (
    <div>
      <div className={ grid["col-md-3"] }>
        <AwaitingProcessing
          focusedDocument={ props.focusedDocument }
          onFocusDocument={ props.onFocusDocument }
          pendingDocuments={ props.pendingDocuments }
        />
      </div>

      <div className={ `${grid["col-md-4"]} ${styles["processing-fields"]}` }>
        <CancelProcessingModal
          focusedDocument={ props.focusedDocument }
          isVisible={ props.focusedDocument.processingState !== "NEEDS_USER_PROCESSING" }
          onCancelProcessing={ props.onCancelProcessing }
        />
        <ProcessingFields
          accountId={ props.accountId }
          categories={ props.categories }
          currencies={ props.currencies }
          documentType={ props.documentType }
          focusedDocument={ props.focusedDocument }
          onChangeDocumentType={ props.onChangeDocumentType }
          onCreateCategory={ props.onCreateCategory }
          onReprocessPendingDocument={ props.onReprocessPendingDocument }
          onSavePendingDocument={ props.onSavePendingDocument }
          onSavePendingOtherDocument={ props.onSavePendingOtherDocument }
          onShowTrashDocument={ props.onShowTrashDocument }
          preferredCurrency={ props.preferredCurrency }
          taxRate={ props.taxRate }
          vendors={ props.vendors }
        />
      </div>
      <div className={ `${grid["col-md-5"]} ${styles["processing-viewer"]}` }>
        <ProcessingViewer
          focusedDocument={ props.focusedDocument }
        />
      </div>

      <TrashDocumentModal
        accountId={ props.accountId }
        focusedDocument={ props.focusedDocument }
        isTrashDocumentVisible={ props.isTrashDocumentVisible }
        onHideTrashDocument={ props.onHideTrashDocument }
        onTrashDocument={ props.onTrashPendingDocument }
      />
    </div>
  );
};

PendingDocumentsProcessingArea.defaultProps = {
  focusedDocument: null,
  pendingDocuments: []
};

PendingDocumentsProcessingArea.propTypes = {
  accountId: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(categoryType).isRequired,
  currencies: PropTypes.arrayOf(currencyType).isRequired,
  documentType: PropTypes.string.isRequired,
  focusedDocument: documentPropType,
  isTrashDocumentVisible: PropTypes.bool.isRequired,
  onCancelProcessing: PropTypes.func.isRequired,
  onChangeDocumentType: PropTypes.func.isRequired,
  onCreateCategory: PropTypes.func.isRequired,
  onFocusDocument: PropTypes.func.isRequired,
  onHideTrashDocument: PropTypes.func.isRequired,
  onReprocessPendingDocument: PropTypes.func.isRequired,
  onSavePendingDocument: PropTypes.func.isRequired,
  onSavePendingOtherDocument: PropTypes.func.isRequired,
  onShowTrashDocument: PropTypes.func.isRequired,
  onTrashPendingDocument: PropTypes.func.isRequired,
  pendingDocuments: PropTypes.arrayOf(documentPropType),
  preferredCurrency: PropTypes.string.isRequired,
  taxRate: PropTypes.number.isRequired,
  vendors: PropTypes.arrayOf(vendorType).isRequired
};



// WEBPACK FOOTER //
// ./src/js/components/upload-documents/pending-documents.jsx